'use client';

import React, {Fragment, ReactNode} from 'react';
import {documentToReactComponents, Options} from '@contentful/rich-text-react-renderer';
import {Block, BLOCKS, Document, Inline, INLINES, MARKS} from '@contentful/rich-text-types';
import {Box, Typography} from '@mui/material';
import {StyledRichTextLink, StyledRichTextInternalLink, StyledRichTextMediaContainer, StyledRichTextTableWrapper, StyledRichTextCtaArea} from '@/components/contentful/RichTextRenderer.styles';
import {RichTextAssetFragment} from '@generated/graphql';
import {
  StyledInformationLinkButton,
} from '@/components/sections/information-link-button-section/InformationLinkButtonSection.styles';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import Video from '@/components/ui/video/Video';
import AssetDownloadLink from '@/components/contentful/AssetDownloadLink';
import {useEmbeddedEntryPath, useRichTextRefs} from '@/components/contentful/RichTextRefsContext';
import ConditionalLinkButton from '@/components/ui/buttons/ConditionalLinkButton';
import {isRenderableLinkButton} from '@/lib/ensure-link-button';
import {FaqSection} from '@/components/sections/faq-section/FaqSection';

interface RichTextRendererProps {
  richTextDocument?: Document | null;
  useSecondaryLinkColor?: boolean;
}

type RichTextAssetWithRequiredFields = RichTextAssetFragment & {
    url: string;
};

const isAssetWithRequiredFields = (asset: RichTextAssetFragment): asset is RichTextAssetWithRequiredFields => {
  return !!asset.url
}

export const RichTextRenderer: React.FC<RichTextRendererProps> = ({ richTextDocument, useSecondaryLinkColor }) => {
  const {assetsById: assetMap, entriesById: entryMap} = useRichTextRefs();
  const embeddedEntryPath = useEmbeddedEntryPath();
  if (!richTextDocument) return null;

  const options: Options = {
    renderText: (text) =>
      text.split('\n').reduce<React.ReactNode[]>((acc, part, index) => {
        if (index > 0) acc.push(<br key={`rt-br-${index}`} />);
        acc.push(part);
        return acc;
      }, []),
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: ReactNode) => {
        const blockNode = node as Block;
        const isEmpty = blockNode.content.every(
          (child) => child.nodeType === 'text' && child.value === ''
        );

        if (isEmpty) {
          const isLast = richTextDocument.content[richTextDocument.content.length - 1] === node;
          if (isLast) return null;
          return (
            <Typography variant='body1' sx={{ mb: 2, minHeight: '1em' }} aria-hidden>
              &nbsp;
            </Typography>
          );
        }
        return (
          <Typography variant='body1' sx={{ mb: 2 }}>
            {children}
          </Typography>
        );
      },
      [BLOCKS.UL_LIST]: (_node: Block | Inline, children: ReactNode) => (
        <Typography component='ul' variant='body1' sx={{ mb: 2, pl: 3, mt: 0 }}>
          {children}
        </Typography>
      ),
      [BLOCKS.OL_LIST]: (_node: Block | Inline, children: ReactNode) => (
        <Typography component='ol' variant='body1' sx={{ mb: 2, pl: 3, mt: 0 }}>
          {children}
        </Typography>
      ),
      [BLOCKS.LIST_ITEM]: (node: Block | Inline) => {
        // Contentful represents list item text as nested PARAGRAPH blocks.
        // We explicitly render those paragraphs inline so we don't get paragraph margins inside <li>.
        const listItemNode = node as Block;
        const normalizedChildren = listItemNode.content?.map((child, index) => (
          <Fragment key={child?.data?.target?.sys?.id ?? `li-${index}`}>
            {documentToReactComponents(child as unknown as Document, {
              ...options,
              renderNode: {
                ...options.renderNode,
                [BLOCKS.PARAGRAPH]: (_pNode: Block | Inline, pChildren: ReactNode) => (
                  <Typography component='span' variant='body1'>
                    {pChildren}
                  </Typography>
                ),
              },
            })}
          </Fragment>
        ));

        return <li>{normalizedChildren}</li>;
      },
      [BLOCKS.QUOTE]: (_node: Block | Inline, children: ReactNode) => {
        return (
          <Typography component='blockquote' variant='body2' sx={{ mb: 2,fontStyle: 'italic' }}>
            {children}
          </Typography>
        );
      },
      [BLOCKS.HEADING_1]: (node: Block | Inline, children: ReactNode) => {
        return (
          <Typography variant='h1' gutterBottom>
            {children}
          </Typography>
        );
      },
      [BLOCKS.HEADING_2]: (node: Block | Inline, children: ReactNode) => {
        return (
          <Typography variant='h2' gutterBottom>
            {children}
          </Typography>
        );
      },
      [BLOCKS.HEADING_3]: (node: Block | Inline, children: ReactNode) => {
        return (
          <Typography variant='h3' gutterBottom>
            {children}
          </Typography>
        );
      },
      [BLOCKS.HEADING_4]: (node: Block | Inline, children: ReactNode) => {
        return (
          <Typography variant='h4' gutterBottom>
            {children}
          </Typography>
        );
      },
      [BLOCKS.HEADING_5]: (node: Block | Inline, children: ReactNode) => {
        return (
          <Typography variant='h5' gutterBottom>
            {children}
          </Typography>
        );
      },
      [BLOCKS.HEADING_6]: (node: Block | Inline, children: ReactNode) => {
        return (
          <Typography variant='caption' component='h6' gutterBottom style={{display: 'block'}}>
            {children}
          </Typography>
        );
      },
      [BLOCKS.TABLE]: (_node: Block | Inline, children: ReactNode) => (
        <StyledRichTextTableWrapper>
          <table>
            <tbody>{children}</tbody>
          </table>
        </StyledRichTextTableWrapper>
      ),
      [BLOCKS.TABLE_ROW]: (_node: Block | Inline, children: ReactNode) => (
        <tr>{children}</tr>
      ),
      [BLOCKS.TABLE_HEADER_CELL]: (node: Block | Inline) => {
        const cellNode = node as Block;
        const hasContent = (cellNode.content ?? []).length > 0;
        const cellContent = hasContent ? cellNode.content.map((child, index) => (
          <Fragment key={`th-${index}`}>
            {documentToReactComponents(child as unknown as Document, {
              ...options,
              renderNode: {
                ...options.renderNode,
                [BLOCKS.PARAGRAPH]: (pNode: Block | Inline, pChildren: ReactNode) => {
                  const isEmpty = ((pNode as Block).content ?? []).every(
                    (c) => c.nodeType === 'text' && c.value === ''
                  );
                  return (
                    <Typography component='span' variant='body1' sx={{ fontWeight: 600 }}>
                      {isEmpty ? ' ' : pChildren}
                    </Typography>
                  );
                },
              },
            })}
          </Fragment>
        )) : ' ';
        return <th>{cellContent}</th>;
      },
      [BLOCKS.TABLE_CELL]: (node: Block | Inline) => {
        const cellNode = node as Block;
        const hasContent = (cellNode.content ?? []).length > 0;
        const cellContent = hasContent ? cellNode.content.map((child, index) => (
          <Fragment key={`td-${index}`}>
            {documentToReactComponents(child as unknown as Document, {
              ...options,
              renderNode: {
                ...options.renderNode,
                [BLOCKS.PARAGRAPH]: (pNode: Block | Inline, pChildren: ReactNode) => {
                  const isEmpty = ((pNode as Block).content ?? []).every(
                    (c) => c.nodeType === 'text' && c.value === ''
                  );
                  return (
                    <Typography component='span' variant='body2'>
                      {isEmpty ? ' ' : pChildren}
                    </Typography>
                  );
                },
              },
            })}
          </Fragment>
        )) : ' ';
        return <td>{cellContent}</td>;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: Block | Inline) => {
        const targetId = node?.data?.target?.sys?.id;
        const entry = targetId ? entryMap.get(targetId) : null;
        if (!entry || embeddedEntryPath.has(entry.sys.id)) return null;

        if (entry.__typename === 'TopicCtaArea') {
          return (
            <StyledRichTextCtaArea>
              {entry.heading && (
                <Typography variant='h5' gutterBottom>
                  {entry.heading}
                </Typography>
              )}
              {entry.breadText && (
                <Typography variant='body2' sx={{whiteSpace: 'pre-line'}}>
                  {entry.breadText}
                </Typography>
              )}
              {isRenderableLinkButton(entry.linkTo) && (
                <Box sx={{mt: 2}}>
                  <ConditionalLinkButton
                    button={entry.linkTo}
                    ButtonComponent={StyledInformationLinkButton}
                  />
                </Box>
              )}
            </StyledRichTextCtaArea>
          );
        }

        if (entry.__typename === 'FaqSection') {
          return <FaqSection section={entry} embedded />;
        }

        return null;
      },
      [BLOCKS.EMBEDDED_ASSET]: (node: Block | Inline) => {
        const targetId = node?.data?.target?.sys?.id;
        const asset = targetId ? assetMap.get(targetId) : null;
        
        if(asset && asset?.contentType?.includes('image') && isAssetWithRequiredFields(asset)) {
          return  <StyledRichTextMediaContainer>
            <ContentfulImage
              src={asset.url}
              alt={asset.title ?? ''}
              width={asset.width ?? 500}
              height={asset.height ?? 300}
              style={{ maxWidth: '100%', maxHeight: 400, width: 'auto', height: 'auto', borderRadius: 'inherit', display: 'block' }}
              quality={75}
              preload
            />
          </StyledRichTextMediaContainer>
        }
        
        if(asset && asset?.contentType?.includes('vide') && isAssetWithRequiredFields(asset)) {
          return  <StyledRichTextMediaContainer sx={{ '& video': { objectFit: 'contain' } }}>
            <Video
              src={asset.url}
              fillViewport={false}
              autoPlay={false}
              controls
            />
          </StyledRichTextMediaContainer>
        }
        
        return null;
      },
      [INLINES.ASSET_HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
        const inlineNode = node as Inline;
        const targetId = inlineNode.data?.target?.sys?.id;
        const asset = targetId ? assetMap.get(targetId) : null;
        if (!asset?.url) return <>{children}</>;
        return (
          <AssetDownloadLink
            href={asset.url}
            useSecondaryLinkColor={useSecondaryLinkColor}
          >
            {children}
          </AssetDownloadLink>
        );
      },
      [INLINES.HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
        const inlineNode = node as Inline;
        const href = inlineNode.data?.uri;
        if (!href) return <>{children}</>;
        const isExternal = /^https?:\/\//i.test(href);
        if (isExternal) {
          return (
            <StyledRichTextLink
              useSecondaryLinkColor={useSecondaryLinkColor}
              href={href}
              target='_blank'
              rel='noopener noreferrer'
            >
              {children}
            </StyledRichTextLink>
          );
        }
        return (
          <StyledRichTextInternalLink
            useSecondaryLinkColor={useSecondaryLinkColor}
            href={href}
          >
            {children}
          </StyledRichTextInternalLink>
        );
      },
      [INLINES.ENTRY_HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
        const inlineNode = node as Inline;
        const targetId = inlineNode.data?.target?.sys?.id;
        const entry = targetId ? entryMap.get(targetId) : null;
        
        if (!entry) return <>{children}</>;

        if (entry.__typename === 'MediaUpload') {
          const fileUrl = entry.mediaFiles?.[0]?.url;
          if (!fileUrl) return <>{children}</>;
          return (
            <AssetDownloadLink
              href={fileUrl}
              useSecondaryLinkColor={useSecondaryLinkColor}
            >
              {children}
            </AssetDownloadLink>
          );
        }

        if (entry.__typename === 'ActiveLink') {
          const href = entry.activeLinkUrl ?? entry.matchingUrlPath;
          if (!href) return <>{children}</>;
          const isExternal = /^https?:\/\//i.test(href);
          if (isExternal) {
            return (
              <StyledRichTextLink
                useSecondaryLinkColor={useSecondaryLinkColor}
                href={href}
                target='_blank'
                rel='noopener noreferrer'
              >
                {children}
              </StyledRichTextLink>
            );
          }
          return (
            <StyledRichTextInternalLink
              useSecondaryLinkColor={useSecondaryLinkColor}
              href={href}
            >
              {children}
            </StyledRichTextInternalLink>
          );
        }

        return <>{children}</>;
      },
    },
    renderMark: {
      [MARKS.BOLD]: (text) => <strong>{text}</strong>,
      [MARKS.ITALIC]: (text) => <em>{text}</em>,
    },
  };

  return <>{documentToReactComponents(richTextDocument, options)}</>;
};
